import React, { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GenericListPage } from '../../components/generic';
import type { UnifiedFilter } from '../../components/generic/FilterSection';
import { useClientInvoices, ClientInvoice } from './useData';
import { moduleConfig, getColumns, getTableActions, getToolbarIcon } from './config';
import ClientInvoicePaymentRemindersPanel from './ClientInvoicePaymentRemindersPanel';

const List: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [remindersPanelOpen, setRemindersPanelOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<ClientInvoice | null>(null);
  const { mutate } = useClientInvoices();

  const initialUnifiedFilters = useMemo<Record<string, UnifiedFilter> | undefined>(() => {
    const raw = searchParams.get('clientId');
    if (!raw) return undefined;
    const clientId = parseInt(raw, 10);
    if (Number.isNaN(clientId)) return undefined;
    const filters: Record<string, UnifiedFilter> = {
      clientName: { type: 'multiSelect', values: [`Client #${clientId}`] },
    };
    const outstanding = searchParams.get('outstanding');
    const paid = searchParams.get('paid');
    if (outstanding === '1' || outstanding === 'true') {
      filters.status = { type: 'multiSelect', values: ['Unpaid', 'Partial'] };
    } else if (paid === '1' || paid === 'true') {
      filters.status = { type: 'multiSelect', values: ['Paid'] };
    }
    return filters;
  }, [searchParams]);

  const openPaymentRemindersPanel = useCallback((invoice: ClientInvoice) => {
    setSelectedInvoice(invoice);
    setRemindersPanelOpen(true);
  }, []);

  const closePaymentRemindersPanel = useCallback(() => {
    setRemindersPanelOpen(false);
    setSelectedInvoice(null);
  }, []);

  return (
    <>
      <GenericListPage<ClientInvoice>
        useData={useClientInvoices}
        listContext={{ onOpenPaymentRemindersPanel: openPaymentRemindersPanel }}
        initialUnifiedFilters={initialUnifiedFilters}
        config={{
          ...moduleConfig,
          getColumns,
          getTableActions,
          getToolbarIcon,
        }}
      />
      <ClientInvoicePaymentRemindersPanel
        open={remindersPanelOpen}
        onClose={closePaymentRemindersPanel}
        invoice={selectedInvoice}
        onReminderAdded={mutate}
        variant="modal"
      />
    </>
  );
};

export default List;

